import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';

// Type guard for axios-like errors
interface AxiosLikeError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      error?: string;
    };
  };
  message?: string;
}

function getErrorMessage(error: unknown, defaultMessage: string): string {
  const err = error as AxiosLikeError;
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || defaultMessage;
}

function getErrorStatus(error: unknown): number | undefined {
  const err = error as AxiosLikeError;
  return err?.response?.status;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  stripePriceId: string;
  priceMonthly: number;
  priceAnnual: number;
  meetupLimit: number;
  friendLimit: number;
  features: Record<string, boolean>;
  active: boolean;
}

interface TrialInfo {
  isOnTrial: boolean;
  trialStartAt: string | null;
  trialEndAt: string | null;
  trialDurationDays: number;
  daysRemaining: number;
  trialConverted: boolean;
  isExpiringSoon: boolean;
}

interface TrialConfig {
  planId: string;
  planName: string;
  trialDays: number;
  trialFeatures: Record<string, boolean>;
}

interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'paused' | 'trialing';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  pausedAt?: string;
  pauseResumesAt?: string;
  hasActiveDiscount?: boolean;
  discountDetails?: {
    type: string;
    appliedAt: string;
    stripeCouponId: string;
    description: string;
  };
  isPaused?: boolean;
  pauseDetails?: {
    behavior: string;
    resumesAt?: string;
  };
  trialInfo?: TrialInfo | null;
  plan?: SubscriptionPlan;
}

interface MonthlyUsage {
  id: string;
  userId: string;
  monthYear: string;
  meetupsCreated: number;
  maxFriendsUsed: number;
}

interface SubscriptionContextValue {
  // Data
  subscription: UserSubscription | null;
  plan: SubscriptionPlan | null;
  usage: MonthlyUsage | null;
  availablePlans: SubscriptionPlan[];

  // State
  loading: boolean;
  error: string | null;
  isActive: boolean;
  isBetaUser: boolean;

  // Trial state
  trialInfo: TrialInfo | null;
  trialOptions: { eligible: boolean; plans: TrialConfig[] } | null;
  isOnTrial: boolean;
  isTrialEligible: boolean;
  isStartingTrial: boolean;
  isConvertingTrial: boolean;
  isCancelingTrial: boolean;

  // Loading states for specific actions
  isResuming: boolean;
  isReactivating: boolean;
  isCanceling: boolean;
  isBillingPortalLoading: boolean;

  // Success feedback
  successMessage: string | null;
  clearSuccessMessage: () => void;

  // Actions
  upgradeToProPlan: () => void;
  cancelSubscription: () => void;
  confirmCancelSubscription: () => void;
  reactivateSubscription: () => void;
  resumeSubscription: () => void;
  openBillingPortal: () => void;

  // Trial actions
  startTrial: (planId: string, durationDays?: number) => void;
  convertTrial: (billingCycle?: 'monthly' | 'annual') => void;
  cancelTrial: () => void;

  // Modal state
  showCancelModal: boolean;
  setShowCancelModal: (show: boolean) => void;

  // Helpers
  hasFeature: (feature: string) => boolean;
  canCreateMeetup: () => boolean;
  canAddFriends: (count: number) => boolean;
  isProUser: () => boolean;
  getUsagePercentage: () => number;
  getDaysUntilRenewal: () => number;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

export const useSubscription = (): SubscriptionContextValue => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const clearSuccessMessage = () => setSuccessMessage(null);
  
  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const queryClient = useQueryClient();

  // Fetch available plans
  const { data: availablePlans = [] } = useQuery({
    queryKey: ['subscription', 'plans'],
    queryFn: async () => {
      const response = await apiClient.api.get('/v1/subscription/plans');
      return response.data.data || [];
    },
    staleTime: 1000 * 60 * 30,
  });

  // Fetch user subscription
  const { data: subscription, isLoading: subscriptionLoading } = useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: async () => {
      try {
        const response = await apiClient.api.get('/v1/subscription/my-subscription');
        return response.data.data || null;
      } catch (error: unknown) {
        const status = getErrorStatus(error);
        if (status === 401 || status === 404) {
          // User has no subscription or not authenticated, return null (free tier)
          return null;
        }
        throw error;
      }
    },
    staleTime: 1000 * 30, // 30 seconds - refetch after webhook updates
    refetchOnWindowFocus: true,
    retry: (failureCount, error: unknown) => {
      // Don't retry on auth errors
      const status = getErrorStatus(error);
      if (status === 401 || status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: 1000,
  });

  // Fetch usage data
  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['subscription', 'usage'],
    queryFn: async () => {
      try {
        const response = await apiClient.api.get('/v1/subscription/usage');
        return response.data.data || null;
      } catch (error: unknown) {
        const status = getErrorStatus(error);
        if (status === 401 || status === 404) {
          // User has no subscription or not authenticated, return null (free tier)
          return null;
        }
        throw error;
      }
    },
    retry: (failureCount, error: unknown) => {
      // Don't retry on auth errors
      const status = getErrorStatus(error);
      if (status === 401 || status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: 1000,
  });

  // Fetch trial options (eligibility and available plans)
  const { data: trialOptions } = useQuery({
    queryKey: ['subscription', 'trial-options'],
    queryFn: async () => {
      try {
        const response = await apiClient.api.get('/v1/subscription/trial/options');
        return response.data.data || { eligible: false, plans: [] };
      } catch {
        return { eligible: false, plans: [] };
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Extract subscription and plan from API response
  const actualSubscription = subscription?.subscription || null;
  const plan = subscription?.plan || availablePlans.find((p: SubscriptionPlan) => p.name === 'Hangout Essentials') || null;
  const isBetaUser = subscription?.isBetaUser || false;

  // Extract trial info from subscription
  const trialInfo: TrialInfo | null = actualSubscription?.trialInfo || null;
  const isOnTrial = trialInfo?.isOnTrial || actualSubscription?.status === 'trialing';
  const isTrialEligible = trialOptions?.eligible || false;

  // Check if subscription is active (including beta users and trials)
  const isActive = (actualSubscription?.status === 'active' && !!actualSubscription) || isBetaUser || !!isOnTrial;

  // Loading state
  const loading = subscriptionLoading || usageLoading;



  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.api.post('/v1/subscription/cancel');
      return response.data;
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      // Show success notification
      setError(null);
    },
    onError: (error: unknown) => {
      setError(getErrorMessage(error, 'Failed to cancel subscription'));
    },
  });

  // Reactivate mutation (for canceled subscriptions)
  const reactivateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.api.post('/v1/subscription/reactivate');
      return response.data;
    },
    onSuccess: () => {
      setError(null);
      setSuccessMessage('✨ Subscription reactivated successfully! Welcome back to Social Squad.');
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (error: unknown) => {
      setError(getErrorMessage(error, 'Failed to reactivate subscription'));
      setSuccessMessage(null);
    },
  });

  // Resume mutation (for paused subscriptions)
  const resumeMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.api.post('/v1/subscription/resume');
      return response.data;
    },
    onSuccess: () => {
      setError(null);
      setSuccessMessage('🎉 Subscription resumed successfully! Your Social Squad features are now active.');
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (error: unknown) => {
      setError(getErrorMessage(error, 'Failed to resume subscription'));
      setSuccessMessage(null);
    },
  });

  const billingPortalMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.api.post('/v1/subscription/billing-portal', {
        return_url: window.location.href,
      });
      return response.data.data.url;
    },
    onSuccess: (url: string) => {
      // Open Stripe Customer Portal in current tab
      window.location.href = url;
    },
    onError: (error: unknown) => {
      setError(getErrorMessage(error, 'Failed to open billing portal'));
    },
  });

  // Trial: Start mutation
  const startTrialMutation = useMutation({
    mutationFn: async ({ planId, durationDays }: { planId: string; durationDays?: number }) => {
      const response = await apiClient.api.post('/v1/subscription/trial/start', {
        planId,
        durationDays,
      });
      return response.data;
    },
    onSuccess: () => {
      setError(null);
      setSuccessMessage('Your free trial has started! Enjoy all premium features.');
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (error: unknown) => {
      setError(getErrorMessage(error, 'Failed to start trial'));
      setSuccessMessage(null);
    },
  });

  // Trial: Convert mutation
  const convertTrialMutation = useMutation({
    mutationFn: async (billingCycle: string = 'monthly') => {
      const response = await apiClient.api.post('/v1/subscription/trial/convert', {
        billingCycle,
      });
      return response.data;
    },
    onSuccess: () => {
      setError(null);
      setSuccessMessage('Trial converted to paid subscription successfully!');
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (error: unknown) => {
      setError(getErrorMessage(error, 'Failed to convert trial'));
      setSuccessMessage(null);
    },
  });

  // Trial: Cancel mutation
  const cancelTrialMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.api.post('/v1/subscription/trial/cancel');
      return response.data;
    },
    onSuccess: () => {
      setError(null);
      setSuccessMessage('Trial canceled. You have been moved to the free plan.');
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
    onError: (error: unknown) => {
      setError(getErrorMessage(error, 'Failed to cancel trial'));
      setSuccessMessage(null);
    },
  });

  // Helper functions
  const hasFeature = (feature: string): boolean => {
    if (!plan) return false;
    return plan.features[feature] === true;
  };

  const canCreateMeetup = (): boolean => {
    if (!plan || !usage) return false;
    return usage.meetupsCreated < plan.meetupLimit;
  };

  const canAddFriends = (count: number): boolean => {
    if (!plan) return false;
    // count is max_participants which includes the creator
    // So if friendLimit is 2, user can have up to 3 total (creator + 2 friends)
    return count <= plan.friendLimit + 1;
  };

  const isProUser = (): boolean => {
    return plan?.name === 'Social Squad' && (actualSubscription?.status === 'active' || isBetaUser);
  };

  const getUsagePercentage = (): number => {
    if (!plan || !usage) return 0;
    return Math.min((usage.meetupsCreated / plan.meetupLimit) * 100, 100);
  };

  const getDaysUntilRenewal = (): number => {
    if (!actualSubscription?.currentPeriodEnd) return 0;
    const endDate = new Date(actualSubscription.currentPeriodEnd);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Action handlers
  const upgradeToProPlan = () => {
    const proPlan = availablePlans.find((p: SubscriptionPlan) => p.name === 'Social Squad');
    if (!proPlan) {
      setError('Pro plan not found');
      return;
    }

    // This will typically redirect to a payment form
    window.location.href = `/pricing?plan=${proPlan.id}`;
  };

  const cancelSubscription = () => {
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async () => {
    return new Promise((resolve, reject) => {
      cancelMutation.mutate(undefined, {
        onSuccess: (data) => {
          setShowCancelModal(false);
          resolve(data);
        },
        onError: (error) => {
          reject(error);
        }
      });
    });
  };

  const reactivateSubscription = () => {
    reactivateMutation.mutate();
  };

  const resumeSubscription = () => {
    resumeMutation.mutate();
  };

  const openBillingPortal = () => {
    billingPortalMutation.mutate();
  };

  const startTrial = (planId: string, durationDays?: number) => {
    startTrialMutation.mutate({ planId, durationDays });
  };

  const convertTrial = (billingCycle: 'monthly' | 'annual' = 'monthly') => {
    convertTrialMutation.mutate(billingCycle);
  };

  const cancelTrial = () => {
    cancelTrialMutation.mutate();
  };

  const value: SubscriptionContextValue = {
    // Data
    subscription: actualSubscription,
    plan,
    usage,
    availablePlans,

    // State
    loading,
    error,
    isActive,
    isBetaUser,

    // Trial state
    trialInfo,
    trialOptions: trialOptions || null,
    isOnTrial: !!isOnTrial,
    isTrialEligible,
    isStartingTrial: startTrialMutation.isPending,
    isConvertingTrial: convertTrialMutation.isPending,
    isCancelingTrial: cancelTrialMutation.isPending,

    // Loading states for specific actions
    isResuming: resumeMutation.isPending,
    isReactivating: reactivateMutation.isPending,
    isCanceling: cancelMutation.isPending,
    isBillingPortalLoading: billingPortalMutation.isPending,

    // Success feedback
    successMessage,
    clearSuccessMessage,

    // Actions
    upgradeToProPlan,
    cancelSubscription,
    confirmCancelSubscription,
    reactivateSubscription,
    resumeSubscription,
    openBillingPortal,

    // Trial actions
    startTrial,
    convertTrial,
    cancelTrial,

    // Modal state
    showCancelModal,
    setShowCancelModal,

    // Helpers
    hasFeature,
    canCreateMeetup,
    canAddFriends,
    isProUser,
    getUsagePercentage,
    getDaysUntilRenewal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
